import type { GameMeta } from '@gmp/game-sdk';

/**
 * The mini-games, as menus and the setup sheet need them (no rules, no content). Order here is the order
 * the playlist builder lists them in.
 */
export const MINI_IDS = [
  'quick-draw', 'closest-call', 'odd-one-out', 'crowd-read', 'call-out',
  'flashback', 'big-talk', 'handshake', 'target-rush', 'number-crunch',
] as const;
export type MiniId = (typeof MINI_IDS)[number];
/** The optional final for the top two. Never in a playlist. */
export type StageId = MiniId | 'showdown';

export type MiniKind = 'Reflex' | 'Trivia' | 'Guessing' | 'Social' | 'Bluffing' | 'Memory' | 'Speed';

export interface MiniInfo {
  id: StageId;
  name: string;
  kind: MiniKind;
  /** One sentence: the whole rule. */
  blurb: string;
  /** The three-second hint shown under the demo on the intro card. */
  hint: string;
  color: string;
}

export const MINI_INFO: Record<StageId, MiniInfo> = {
  'quick-draw': { id: 'quick-draw', name: 'Quick Draw', kind: 'Reflex', color: '#ff5a4e', blurb: 'Wait for the panel to flip to GO, then tap first. Jump the gun and you lose points.', hint: 'Three draws. Red means wait, green means tap.' },
  'closest-call': { id: 'closest-call', name: 'Closest Call', kind: 'Guessing', color: '#3ab0ff', blurb: 'Slide to your best guess at the number. Closest guess wins, dead-on scores a bullseye.', hint: 'Two questions. Nobody expects you to know it exactly.' },
  'odd-one-out': { id: 'odd-one-out', name: 'Odd One Out', kind: 'Trivia', color: '#ffc233', blurb: 'Four things, one doesn\'t belong. Tap it, and the quicker you are the more it\'s worth.', hint: 'Three rounds of twenty seconds. A wrong tap scores nothing.' },
  'crowd-read': { id: 'crowd-read', name: 'Crowd Read', kind: 'Social', color: '#b46cff', blurb: 'Agree or disagree, then guess how many of the room agree with the statement.', hint: 'Your guess scores, not your opinion. Read the room.' },
  'call-out': { id: 'call-out', name: 'Call Out', kind: 'Social', color: '#ff7a1a', blurb: 'Secretly call out one player to take points off them. If they called you out too, you both win.', hint: 'The most called-out player gets a bonus for being feared.' },
  flashback: { id: 'flashback', name: 'Flashback', kind: 'Memory', color: '#2fd6a8', blurb: 'A pattern lights up for three seconds. Tap the same squares from memory.', hint: 'Right squares score, wrong ones cost you. Then a bigger grid.' },
  'big-talk': { id: 'big-talk', name: 'Big Talk', kind: 'Bluffing', color: '#ff4fa3', blurb: 'You get a secret number. Claim any number you like, then doubt the player you think is talking big.', hint: 'Nobody doubts you? Your claim pays out, true or not.' },
  handshake: { id: 'handshake', name: 'Handshake Deal', kind: 'Bluffing', color: '#8bd43a', blurb: 'You and your rival secretly choose: shake and share the pot, or snatch it all.', hint: 'Both shake: 100 each. One snatches: 200 to them. Both snatch: nothing.' },
  'target-rush': { id: 'target-rush', name: 'Target Rush', kind: 'Speed', color: '#ff3d6e', blurb: 'Tap the lit square, again and again. First to fifteen wins; a wrong tap knocks you back one.', hint: 'Accuracy beats mashing.' },
  'number-crunch': { id: 'number-crunch', name: 'Number Crunch', kind: 'Speed', color: '#30c5ff', blurb: 'Quick sums, four answers each. Get through as many as you can before time runs out.', hint: 'A wrong answer costs a little and moves you on.' },
  showdown: { id: 'showdown', name: 'The Showdown', kind: 'Trivia', color: '#ffd23f', blurb: 'The top two go head to head: first right answer takes the point, first to three wins.', hint: 'Everyone else backs a finalist for a bonus.' },
};

/** Catalog data only (no rules or content), so menus can show the game without loading it. */
export const grudgeGauntletMeta = {
  id: 'grudge-gauntlet',
  name: 'Grudge Gauntlet',
  tagline: 'Ten tiny games. One rival. Settle the score.',
  description:
    'A run of very short mini-games, each explained in one sentence: reflex draws, number guesses, odd-one-out trivia, bluffing, memory and secret call-outs. Everyone gets a public rival each game and earns a bonus for beating them. Play a random gauntlet, build your own playlist, or replay a favourite again and again. The top two can settle it in a final Showdown.',
  minPlayers: 3,
  maxPlayers: 10,
  estimatedMinutes: [6, 15],
  usesMedia: false,
  modes: [
    { id: 'random', name: 'Random Gauntlet', description: 'A shuffled run of mini-games, no repeats until every one has been played.' },
    { id: 'playlist', name: 'Playlist', description: 'The host picks the mini-games and their order (repeats allowed).' },
    { id: 'single', name: 'Just this one', description: 'One mini-game, played several times in a row.' },
  ],
  theme: { primary: '#ff5a4e', secondary: '#ffd23f', background: '#14112a', ink: '#fff4e0' },
  readiness: 'beta',
  genre: 'Mini-game rivalry gauntlet',
  controls: 'tap',
  category: 'party',
  instructions: [
    { icon: 'Zap', title: 'Tiny games', body: 'Each mini-game takes under a minute and is explained in one sentence. Watch the intro card, then play on your phone.' },
    { icon: 'Users', title: 'Your rival', body: 'Every game you get a new public rival. Score more than them in that game for +50.' },
    { icon: 'Target', title: 'Mixed skills', body: 'Reflexes, guesses, trivia, memory, bluffing and secret call-outs. Nobody is good at everything.' },
    { icon: 'Trophy', title: 'Showdown', body: 'Optionally, the top two finish with a head-to-head Showdown while everyone else backs a winner.' },
  ],
} satisfies GameMeta;
